import { useCallback, useEffect, useRef, useState } from 'react';
import { AI_ERROR, AiChatError, getConversation, sendChatMessage } from '../../services/aiChat';

/**
 * Estado e fluxo do chat do assistente.
 *
 * Mensagem: { id, role: 'user'|'assistant', text, status, tools[], toolLabel,
 *             error, feedback }
 *  - assistant.status: 'streaming' | 'done' | 'error' | 'stopped'
 *  - assistant.error:  { kind: 'quota'|'rate'|'permission'|'session'|'error', message }
 *  - feedback: 'up' | 'down' | null  → SÓ estado local (não há endpoint no fv-ai).
 *
 * `enterprise_id`/`user_id` nunca aparecem aqui: só `conversation_id` e `message`.
 */

let seq = 0;
const novoId = () => {
  seq += 1;
  return `m${Date.now().toString(36)}${seq}`;
};

function classificarErro(codigo, status, mensagem) {
  if (codigo === AI_ERROR.COTA_EXCEDIDA) return { kind: 'quota', message: mensagem };
  if (codigo === AI_ERROR.LIMITE_DE_USO) return { kind: 'rate', message: mensagem };
  if (status === 403 || codigo === AI_ERROR.SEM_PERMISSAO) return { kind: 'permission', message: mensagem };
  if (status === 401 || codigo === AI_ERROR.NAO_AUTORIZADO) return { kind: 'session', message: mensagem };
  return { kind: 'error', message: mensagem };
}

export function useAiChat() {
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const abortRef = useRef(null);
  const convRef = useRef(null);
  const busyRef = useRef(false);

  const messagesRef = useRef([]);

  useEffect(() => { convRef.current = conversationId; }, [conversationId]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  // Ao desmontar, encerra qualquer stream em andamento.
  useEffect(() => () => abortRef.current?.abort(), []);

  const patchAssistant = useCallback((id, patch) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...(typeof patch === 'function' ? patch(m) : patch) } : m)));
  }, []);

  const send = useCallback(async (texto, { replace } = {}) => {
    const pergunta = String(texto || '').trim();
    if (!pergunta || busyRef.current) return;

    busyRef.current = true;
    setBusy(true);
    setLoadError(null);

    const assistantId = novoId();
    const userMsg = { id: novoId(), role: 'user', text: pergunta };
    const assistantMsg = { id: assistantId, role: 'assistant', text: '', status: 'streaming', tools: [], toolLabel: null, error: null, feedback: null, question: pergunta };
    setMessages((prev) => [...(replace ? prev.filter((m) => !replace.includes(m.id)) : prev), userMsg, assistantMsg]);

    const controller = new AbortController();
    abortRef.current = controller;
    let terminou = false;

    try {
      const { aborted } = await sendChatMessage({
        message: pergunta,
        conversationId: convRef.current || undefined,
        signal: controller.signal,
        onToken: (t) => patchAssistant(assistantId, (m) => ({ text: m.text + t, toolLabel: null })),
        onToolStatus: (ev) => patchAssistant(assistantId, (m) => ({
          toolLabel: ev?.tool || 'consulta',
          tools: ev?.tool && !m.tools.includes(ev.tool) ? [...m.tools, ev.tool] : m.tools,
        })),
        onDone: (done) => {
          terminou = true;
          if (done?.conversation_id) {
            convRef.current = done.conversation_id;
            setConversationId(done.conversation_id);
          }
          patchAssistant(assistantId, { status: 'done', toolLabel: null });
        },
        onError: (ev) => {
          terminou = true;
          if (ev?.codigo === 'CANCELADO') {
            patchAssistant(assistantId, { status: 'stopped', toolLabel: null });
            return;
          }
          patchAssistant(assistantId, {
            status: 'error',
            toolLabel: null,
            error: classificarErro(ev?.codigo, 0, ev?.mensagem || 'Não consegui responder agora.'),
          });
        },
      });

      if (aborted) {
        patchAssistant(assistantId, { status: 'stopped', toolLabel: null });
      } else if (!terminou) {
        // Stream fechou sem `done` nem `error`: conexão cortada.
        patchAssistant(assistantId, {
          status: 'error',
          toolLabel: null,
          error: { kind: 'error', message: 'A conexão com o assistente foi interrompida.' },
        });
      }
    } catch (e) {
      const err = e instanceof AiChatError ? e : new AiChatError();
      const erro = classificarErro(err.codigo, err.status, err.message);
      if (erro.kind === 'quota') setQuotaExceeded(true);
      patchAssistant(assistantId, { status: 'error', toolLabel: null, error: erro });
    } finally {
      abortRef.current = null;
      busyRef.current = false;
      setBusy(false);
    }
  }, [patchAssistant]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  /** "Tentar de novo": descarta a pergunta + resposta com erro e reenvia. */
  const retry = useCallback((assistantId) => {
    const atuais = messagesRef.current;
    const i = atuais.findIndex((m) => m.id === assistantId);
    if (i <= 0) return;
    const pergunta = atuais[i].question || atuais[i - 1]?.text;
    send(pergunta, { replace: [atuais[i - 1].id, assistantId] });
  }, [send]);

  const newConversation = useCallback(() => {
    abortRef.current?.abort();
    convRef.current = null;
    setConversationId(null);
    setMessages([]);
    setLoadError(null);
    setQuotaExceeded(false);
  }, []);

  const openConversation = useCallback(async (id) => {
    abortRef.current?.abort();
    setLoadingConversation(true);
    setLoadError(null);
    try {
      const { conversa, mensagens } = await getConversation(id);
      convRef.current = conversa?.id || id;
      setConversationId(conversa?.id || id);
      setMessages(mensagens.map((m) => (m.papel === 'user'
        ? { id: novoId(), role: 'user', text: m.conteudo }
        : { id: novoId(), role: 'assistant', text: m.conteudo, status: 'done', tools: [], toolLabel: null, error: null, feedback: null })));
      return true;
    } catch (e) {
      setLoadError(e instanceof AiChatError ? e.message : 'Não consegui abrir a conversa.');
      return false;
    } finally {
      setLoadingConversation(false);
    }
  }, []);

  /** Feedback 👍/👎 — somente local (pendência: endpoint no fv-ai). Clicar de novo desmarca. */
  const setFeedback = useCallback((id, valor) => {
    patchAssistant(id, (m) => ({ feedback: m.feedback === valor ? null : valor }));
  }, [patchAssistant]);

  /** Chamado quando a conversa aberta é excluída no histórico. */
  const onConversationDeleted = useCallback((id) => {
    if (convRef.current === id) newConversation();
  }, [newConversation]);

  return {
    messages, conversationId, busy, quotaExceeded, loadingConversation, loadError,
    send, stop, retry, newConversation, openConversation, setFeedback, onConversationDeleted,
  };
}
